import { PageHeader } from '@/shared/components/PageHeader';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  RefreshCw,
  Search,
  ShieldCheck,
  SkipForward,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

type TestStatus = 'passed' | 'failed' | 'skipped' | 'pending';

interface TestCase {
  title: string;
  status: TestStatus;
  duration: number; // ms
  error?: string;
  retries?: number;
}

interface TestSuite {
  title: string;
  file: string;
  domain: string;
  layer: 'frontend' | 'backend-api';
  tests: TestCase[];
}

interface TestReport {
  generatedAt: string;
  totalDuration: number;
  suites: TestSuite[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function statusColor(status: TestStatus) {
  switch (status) {
    case 'passed':
      return 'text-green-600 dark:text-green-400';
    case 'failed':
      return 'text-red-600 dark:text-red-400';
    case 'skipped':
      return 'text-yellow-600 dark:text-yellow-400';
    default:
      return 'text-zinc-500';
  }
}

function statusBadgeVariant(status: TestStatus): string {
  switch (status) {
    case 'passed':
      return 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700';
    case 'failed':
      return 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700';
    case 'skipped':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700';
    default:
      return 'bg-zinc-100 text-zinc-800 border-zinc-300';
  }
}

function StatusIcon({ status, className = 'w-4 h-4' }: { status: TestStatus; className?: string }) {
  switch (status) {
    case 'passed':
      return <CheckCircle2 className={`${className} text-green-600 dark:text-green-400`} />;
    case 'failed':
      return <XCircle className={`${className} text-red-600 dark:text-red-400`} />;
    case 'skipped':
      return <SkipForward className={`${className} text-yellow-500 dark:text-yellow-400`} />;
    default:
      return <AlertCircle className={`${className} text-zinc-400`} />;
  }
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatTotalDuration(ms: number): string {
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  return `${mins}m ${secs}s`;
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  sub,
  accent,
  icon,
}: {
  label: string;
  value: string | number;
  sub?: string;
  accent: string;
  icon: React.ReactNode;
}) {
  return (
    <div
      className="bg-card border border-border rounded-lg p-5 border-t-4 theme-transition"
      style={{ borderTopColor: accent }}
    >
      <div className="flex items-start justify-between mb-3">
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center"
          style={{ backgroundColor: `${accent}20`, color: accent }}
        >
          {icon}
        </div>
      </div>
      <div className="text-3xl font-bold text-foreground">{value}</div>
      {sub && <div className="text-sm text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

// ─── Progress bar ─────────────────────────────────────────────────────────────

function PassRateBar({ passed, total }: { passed: number; total: number }) {
  const pct = total === 0 ? 0 : Math.round((passed / total) * 100);
  const color = pct >= 90 ? '#22c55e' : pct >= 70 ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 bg-zinc-100 dark:bg-zinc-800 rounded-full h-2.5 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-sm font-semibold w-10 text-right" style={{ color }}>
        {pct}%
      </span>
    </div>
  );
}

// ─── Suite row (expandable) ───────────────────────────────────────────────────

function SuiteRow({ suite }: { suite: TestSuite }) {
  const [open, setOpen] = useState(false);

  const passed = suite.tests.filter((t) => t.status === 'passed').length;
  const failed = suite.tests.filter((t) => t.status === 'failed').length;
  const skipped = suite.tests.filter((t) => t.status === 'skipped').length;
  const total = suite.tests.length;
  const suiteStatus: TestStatus = failed > 0 ? 'failed' : skipped === total ? 'skipped' : 'passed';
  const totalDuration = suite.tests.reduce((acc, t) => acc + t.duration, 0);

  return (
    <div className="border border-border rounded-lg overflow-hidden theme-transition">
      {/* Suite header */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-card hover:bg-accent transition-colors text-left"
      >
        <StatusIcon status={suiteStatus} className="w-5 h-5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-foreground truncate">{suite.title}</span>
            <Badge
              variant="outline"
              className={`text-xs px-1.5 py-0 ${suite.layer === 'backend-api' ? 'border-purple-300 text-purple-700 dark:text-purple-400' : 'border-blue-300 text-blue-700 dark:text-blue-400'}`}
            >
              {suite.layer === 'backend-api' ? 'API' : 'Frontend'}
            </Badge>
            <span className="text-xs text-muted-foreground font-mono">{suite.file}</span>
          </div>
          <div className="mt-1">
            <PassRateBar passed={passed} total={total} />
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0 text-sm">
          <span className="text-green-600 dark:text-green-400 font-medium">{passed} passed</span>
          {failed > 0 && (
            <span className="text-red-600 dark:text-red-400 font-medium">{failed} failed</span>
          )}
          {skipped > 0 && (
            <span className="text-yellow-600 dark:text-yellow-400 font-medium">
              {skipped} skipped
            </span>
          )}
          <span className="text-muted-foreground flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {formatTotalDuration(totalDuration)}
          </span>
          {open ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Test cases */}
      {open && (
        <div className="border-t border-border bg-zinc-50 dark:bg-zinc-900/50 divide-y divide-border">
          {suite.tests.map((test, i) => (
            <div key={i} className="flex items-start gap-3 px-5 py-2.5">
              <StatusIcon status={test.status} className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <span className={`text-sm ${statusColor(test.status)}`}>{test.title}</span>
                {test.error && (
                  <div className="mt-1 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded px-2 py-1 font-mono leading-relaxed">
                    {test.error}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {test.retries !== undefined && test.retries > 0 && (
                  <span className="text-xs text-yellow-600 dark:text-yellow-400">
                    {test.retries} retr{test.retries === 1 ? 'y' : 'ies'}
                  </span>
                )}
                <span className="text-xs text-muted-foreground font-mono w-12 text-right">
                  {formatDuration(test.duration)}
                </span>
                <span
                  className={`text-xs font-medium px-1.5 py-0.5 rounded border ${statusBadgeVariant(test.status)}`}
                >
                  {test.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Fallback data (used when report JSON hasn't been generated yet) ──────────

const FALLBACK_REPORT: TestReport = {
  generatedAt: new Date().toISOString(),
  totalDuration: 0,
  suites: [
    {
      title: 'No report found',
      file: 'Run `pnpm test:e2e` to generate',
      domain: 'all',
      layer: 'frontend',
      tests: [
        {
          title: 'Run pnpm test:e2e from the works-lite directory to generate a report',
          status: 'pending',
          duration: 0,
        },
      ],
    },
  ],
};

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function TestCoveragePage() {
  const [report, setReport] = useState<TestReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | TestStatus>('all');
  const [layerFilter, setLayerFilter] = useState<'all' | 'frontend' | 'backend-api'>('all');
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  useEffect(() => {
    setLoading(true);
    fetch('/test-results.json')
      .then((r) => {
        if (!r.ok) throw new Error('not found');
        return r.json();
      })
      .then((data: TestReport) => setReport(data))
      .catch(() => setReport(FALLBACK_REPORT))
      .finally(() => setLoading(false));
  }, [lastRefresh]);

  const allTests = useMemo(() => report?.suites.flatMap((s) => s.tests) ?? [], [report]);

  const totals = useMemo(
    () => ({
      total: allTests.length,
      passed: allTests.filter((t) => t.status === 'passed').length,
      failed: allTests.filter((t) => t.status === 'failed').length,
      skipped: allTests.filter((t) => t.status === 'skipped').length,
    }),
    [allTests],
  );

  const passRate = totals.total === 0 ? 0 : Math.round((totals.passed / totals.total) * 100);

  const overallStatus: TestStatus =
    totals.failed > 0 ? 'failed' : totals.skipped === totals.total ? 'skipped' : 'passed';

  const filteredSuites = useMemo(() => {
    if (!report) return [];
    return report.suites
      .filter((s) => {
        if (layerFilter !== 'all' && s.layer !== layerFilter) return false;
        if (statusFilter !== 'all') {
          const hasMatch = s.tests.some((t) => t.status === statusFilter);
          if (!hasMatch) return false;
        }
        if (search) {
          const q = search.toLowerCase();
          const suiteMatch = s.title.toLowerCase().includes(q) || s.file.toLowerCase().includes(q);
          const testMatch = s.tests.some((t) => t.title.toLowerCase().includes(q));
          if (!suiteMatch && !testMatch) return false;
        }
        return true;
      })
      .map((s) => {
        if (!search && statusFilter === 'all') return s;
        return {
          ...s,
          tests: s.tests.filter((t) => {
            const statusMatch = statusFilter === 'all' || t.status === statusFilter;
            const searchMatch =
              !search ||
              t.title.toLowerCase().includes(search.toLowerCase()) ||
              s.title.toLowerCase().includes(search.toLowerCase());
            return statusMatch && searchMatch;
          }),
        };
      });
  }, [report, search, statusFilter, layerFilter]);

  const layerBreakdown = useMemo(() => {
    if (!report) return { frontend: { total: 0, passed: 0 }, api: { total: 0, passed: 0 } };
    const fe = report.suites.filter((s) => s.layer === 'frontend').flatMap((s) => s.tests);
    const api = report.suites.filter((s) => s.layer === 'backend-api').flatMap((s) => s.tests);
    return {
      frontend: {
        total: fe.length,
        passed: fe.filter((t) => t.status === 'passed').length,
      },
      api: {
        total: api.length,
        passed: api.filter((t) => t.status === 'passed').length,
      },
    };
  }, [report]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <PageHeader
        title="Test Coverage"
        subtitle="Playwright E2E test results across frontend and backend APIs"
        icon={<ShieldCheck className="w-8 h-8" style={{ color: '#E84A2B' }} />}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLastRefresh(Date.now())}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-6">
        {/* Last run metadata */}
        {report && (
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <StatusIcon status={overallStatus} className="w-5 h-5" />
            <span>
              Last run:{' '}
              <span className="font-medium text-foreground">
                {new Date(report.generatedAt).toLocaleString()}
              </span>
            </span>
            {report.totalDuration > 0 && (
              <>
                <span>·</span>
                <Clock className="w-4 h-4" />
                <span>
                  Total duration:{' '}
                  <span className="font-medium text-foreground">
                    {formatTotalDuration(report.totalDuration)}
                  </span>
                </span>
              </>
            )}
          </div>
        )}

        {/* KPI row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            label="Pass Rate"
            value={`${passRate}%`}
            sub={`${totals.passed} of ${totals.total} tests`}
            accent={passRate >= 90 ? '#22c55e' : passRate >= 70 ? '#f59e0b' : '#ef4444'}
            icon={<CheckCircle2 className="w-4 h-4" />}
          />
          <KpiCard
            label="Total Tests"
            value={totals.total}
            sub={`across ${report?.suites.length ?? 0} suites`}
            accent="#E84A2B"
            icon={<ShieldCheck className="w-4 h-4" />}
          />
          <KpiCard
            label="Failed"
            value={totals.failed}
            sub={totals.failed > 0 ? 'Action required' : 'All clear'}
            accent={totals.failed > 0 ? '#ef4444' : '#22c55e'}
            icon={<XCircle className="w-4 h-4" />}
          />
          <KpiCard
            label="Skipped"
            value={totals.skipped}
            sub={totals.skipped > 0 ? 'Pending implementation' : 'None'}
            accent="#f59e0b"
            icon={<SkipForward className="w-4 h-4" />}
          />
        </div>

        {/* Layer breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-lg p-5 theme-transition">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm text-foreground">Frontend (Playwright E2E)</h3>
              <Badge
                variant="outline"
                className="border-blue-300 text-blue-700 dark:text-blue-400 text-xs"
              >
                {layerBreakdown.frontend.total} tests
              </Badge>
            </div>
            <PassRateBar
              passed={layerBreakdown.frontend.passed}
              total={layerBreakdown.frontend.total}
            />
            <p className="text-xs text-muted-foreground mt-2">
              {layerBreakdown.frontend.passed} passed /{' '}
              {layerBreakdown.frontend.total - layerBreakdown.frontend.passed} failed or skipped
            </p>
          </div>
          <div className="bg-card border border-border rounded-lg p-5 theme-transition">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm text-foreground">Backend API (Jest E2E)</h3>
              <Badge
                variant="outline"
                className="border-purple-300 text-purple-700 dark:text-purple-400 text-xs"
              >
                {layerBreakdown.api.total} tests
              </Badge>
            </div>
            <PassRateBar passed={layerBreakdown.api.passed} total={layerBreakdown.api.total} />
            <p className="text-xs text-muted-foreground mt-2">
              {layerBreakdown.api.passed} passed /{' '}
              {layerBreakdown.api.total - layerBreakdown.api.passed} failed or skipped
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search suites or tests…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
          <div className="flex items-center gap-1 bg-zinc-100 dark:bg-zinc-800 rounded-lg p-1">
            {(['all', 'passed', 'failed', 'skipped'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors ${
                  statusFilter === s
                    ? 'bg-white dark:bg-zinc-700 text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 bg-zinc-100 dark:bg-zinc-800 rounded-lg p-1">
            {(['all', 'frontend', 'backend-api'] as const).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLayerFilter(l)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors ${
                  layerFilter === l
                    ? 'bg-white dark:bg-zinc-700 text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {l === 'backend-api' ? 'API' : l}
              </button>
            ))}
          </div>
          <span className="text-xs text-muted-foreground">
            {filteredSuites.length} suite{filteredSuites.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Suite list */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-16 rounded-lg bg-zinc-100 dark:bg-zinc-800 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSuites.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <ShieldCheck className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No test suites match your filters.</p>
              </div>
            ) : (
              filteredSuites.map((suite, i) => <SuiteRow key={i} suite={suite} />)
            )}
          </div>
        )}

        {/* Info footer */}
        <div className="border border-border rounded-lg p-4 bg-zinc-50 dark:bg-zinc-900/50 theme-transition">
          <h4 className="text-sm font-semibold text-foreground mb-2">How to update this report</h4>
          <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
            <li>
              Run{' '}
              <code className="bg-zinc-200 dark:bg-zinc-700 px-1 rounded text-xs">
                pnpm test:e2e
              </code>{' '}
              from{' '}
              <code className="bg-zinc-200 dark:bg-zinc-700 px-1 rounded text-xs">
                apps/works-lite
              </code>
            </li>
            <li>
              Then run{' '}
              <code className="bg-zinc-200 dark:bg-zinc-700 px-1 rounded text-xs">
                node scripts/generate-test-report.js
              </code>{' '}
              to publish the results
            </li>
            <li>Click Refresh above to see the latest results</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
